import { useCallback, useEffect, useState } from 'react';

export type MatchReason = 'name' | 'tag' | 'bio' | 'story' | 'related' | 'title' | 'summary' | 'transcript';
export type MatchStrength = 'strong' | 'related';

export type SearchMatchFields = {
  match_reason?: MatchReason;
  match_snippet?: string;
  match_strength?: MatchStrength;
  matched_story_title?: string;
};

export const MATCH_STYLE_VARIANTS = ['none', 'chip', 'ribbon', 'snippet', 'why'] as const;
export type MatchStyleVariant = (typeof MATCH_STYLE_VARIANTS)[number];

export const MATCH_STYLE_OPTIONS: { value: MatchStyleVariant; label: string }[] = [
  { value: 'none', label: 'Current (no match UI)' },
  { value: 'chip', label: 'Reason chip' },
  { value: 'ribbon', label: 'Strength ribbon' },
  { value: 'snippet', label: 'Highlight snippet (list)' },
  { value: 'why', label: 'Why-this card (list)' },
];

export const isExpandedMatchLayout = (variant: MatchStyleVariant): boolean => {
  return variant === 'snippet' || variant === 'why';
};

const STORAGE_KEY = 'kh.searchMatchStyle';

const EXPLAINABLE_REASONS: MatchReason[] = ['name', 'tag', 'bio', 'story', 'title', 'summary', 'transcript'];

export const hasSearchMatch = (match?: SearchMatchFields | null): boolean => {
  return Boolean(match?.match_reason && EXPLAINABLE_REASONS.includes(match.match_reason));
};

export const sanitizeMatchSnippet = (html: string): string => {
  return html.replace(/<(?!\/?em\b)[^>]*>/gi, '');
};

export const stripMatchEmTags = (value: string): string => {
  return value.replace(/<\/?em>/gi, '');
};

const REASON_SHORT: Record<Exclude<MatchReason, 'related'>, string> = {
  name: 'Name',
  tag: 'Topic',
  bio: 'Bio',
  story: 'Story',
  title: 'Title',
  summary: 'Summary',
  transcript: 'Transcript',
};

const RIBBON_DETAIL_MAX = 18;

const shortTopicChipLabel = (match: SearchMatchFields): string => {
  const tag = match.match_snippet ? stripMatchEmTags(match.match_snippet).trim() : '';
  if (tag && tag.length <= RIBBON_DETAIL_MAX) {
    return `Topic · ${tag}`;
  }
  return 'Topic';
};

export const matchChipLabel = (match: SearchMatchFields): string => {
  if (match.match_reason === 'tag') {
    return shortTopicChipLabel(match);
  }
  if (match.match_reason === 'story') {
    return 'Story';
  }
  if (match.match_reason && match.match_reason !== 'related') {
    return REASON_SHORT[match.match_reason];
  }
  return 'Match';
};

/** Short category for the strength ribbon — never repeats "Related", never a long title. */
export const matchRibbonCategory = (match: SearchMatchFields): string | null => {
  if (match.match_reason === 'tag') {
    return shortTopicChipLabel(match);
  }
  if (match.match_reason === 'story') {
    return 'Story';
  }
  if (
    match.match_reason === 'name' ||
    match.match_reason === 'bio' ||
    match.match_reason === 'title' ||
    match.match_reason === 'summary' ||
    match.match_reason === 'transcript'
  ) {
    return REASON_SHORT[match.match_reason];
  }
  return null;
};

export const matchRibbonText = (match: SearchMatchFields): string => {
  const strength = match.match_strength === 'strong' ? 'Strong' : 'Related';
  const category = matchRibbonCategory(match);
  return category ? `${strength} · ${category}` : strength;
};

export const matchSourceLabel = (match: SearchMatchFields): string => {
  if (match.match_reason && match.match_reason !== 'related') {
    return REASON_SHORT[match.match_reason];
  }
  return 'Match';
};

export const matchReasonCopy = (match: SearchMatchFields): string => {
  switch (match.match_reason) {
    case 'name':
      return 'Matched on name';
    case 'tag': {
      const tag = match.match_snippet ? stripMatchEmTags(match.match_snippet) : null;
      return tag ? `Matched on ${tag}` : 'Matched on a topic';
    }
    case 'bio':
      return 'Matched in their bio';
    case 'story':
      return match.matched_story_title ? `Matched in “${match.matched_story_title}”` : 'Matched in a story';
    case 'title':
      return 'Matched on title';
    case 'summary':
      return 'Matched in summary';
    case 'transcript':
      return 'Matched in transcript';
    case 'related':
      return 'Related to your search';
    default:
      return 'Related to your search';
  }
};

export const isMatchStyleVariant = (value: string | null): value is MatchStyleVariant => {
  return MATCH_STYLE_VARIANTS.includes(value as MatchStyleVariant);
};

export const useMatchStyleTrial = (enabled: boolean) => {
  const [matchStyle, setMatchStyleState] = useState<MatchStyleVariant>(() => {
    if (!enabled || typeof window === 'undefined') return 'none';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isMatchStyleVariant(stored) ? stored : 'chip';
  });

  useEffect(() => {
    if (!enabled) {
      setMatchStyleState('none');
      return;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    setMatchStyleState(isMatchStyleVariant(stored) ? stored : 'chip');
  }, [enabled]);

  const setMatchStyle = useCallback((value: MatchStyleVariant) => {
    setMatchStyleState(value);
    window.localStorage.setItem(STORAGE_KEY, value);
  }, []);

  return { matchStyle, setMatchStyle };
};

type MatchSnippetTextProps = {
  html: string;
  className?: string;
};

export const MatchSnippetText = ({ html, className = '' }: MatchSnippetTextProps) => {
  return (
    <span
      className={`[&_em]:bg-accent/25 [&_em]:font-semibold [&_em]:not-italic ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizeMatchSnippet(html) }}
    />
  );
};

type PeerListenerMatchHintProps = {
  match: SearchMatchFields;
  variant: MatchStyleVariant;
  expanded?: boolean;
};

export const PeerListenerMatchRibbon = ({ match }: { match: SearchMatchFields }) => {
  const isStrong = match.match_strength === 'strong';
  const label = matchRibbonText(match);
  const tooltip = matchReasonCopy(match);
  return (
    <div
      className={`flex h-6 w-full items-center justify-center px-2 ${
        isStrong ? 'bg-success text-white' : 'bg-gray-200 text-gray-700'
      }`}
      title={tooltip}
    >
      <p className="font-albert-sans min-w-0 truncate text-[11px] font-medium leading-4">{label}</p>
    </div>
  );
};

export const PeerListenerMatchHint = ({ match, variant, expanded = false }: PeerListenerMatchHintProps) => {
  if (variant === 'none' || !hasSearchMatch(match)) {
    return null;
  }

  const isStrong = match.match_strength === 'strong';
  const source = matchSourceLabel(match);
  const storyTitle = match.match_reason === 'story' ? match.matched_story_title : undefined;
  const snippet = match.match_snippet;

  if (variant === 'chip') {
    return (
      <span
        className={`inline-flex min-w-0 max-w-full overflow-hidden rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 ${
          isStrong ? 'bg-success/15 text-success' : 'bg-gray-200 text-gray-600'
        }`}
        title={matchReasonCopy(match)}
      >
        <span className="min-w-0 truncate">{matchChipLabel(match)}</span>
      </span>
    );
  }

  if (variant === 'snippet') {
    if (expanded) {
      return (
        <div className="h-full min-h-0 w-full overflow-hidden text-left">
          <p className="font-albert-sans text-[10px] font-semibold uppercase tracking-wide text-gray-500">{source}</p>
          {storyTitle ? (
            <p className="font-open-sans text-primary mt-1 line-clamp-2 break-words text-sm font-medium leading-5">
              {storyTitle}
            </p>
          ) : null}
          {snippet ? (
            match.match_reason === 'story' ||
            match.match_reason === 'bio' ||
            match.match_reason === 'summary' ||
            match.match_reason === 'transcript' ? (
              <p className="mt-1 line-clamp-6 w-full break-words text-sm italic leading-5 text-gray-600">
                “<MatchSnippetText html={snippet} />”
              </p>
            ) : (
              <p className="mt-1 line-clamp-3 w-full break-words text-sm leading-5 text-gray-700">
                <MatchSnippetText html={snippet} />
              </p>
            )
          ) : (
            <p className="mt-1 text-sm leading-5 text-gray-500">{matchReasonCopy(match)}</p>
          )}
        </div>
      );
    }

    const alignClass = 'text-center';
    if (snippet) {
      return (
        <p className={`line-clamp-3 ${alignClass} w-full text-sm italic leading-5 text-gray-600`}>
          “<MatchSnippetText html={snippet} />”
        </p>
      );
    }
    if (storyTitle) {
      return (
        <p className={`line-clamp-2 ${alignClass} w-full text-sm italic leading-5 text-gray-600`}>“{storyTitle}”</p>
      );
    }
    return <p className={`${alignClass} w-full text-sm leading-5 text-gray-500`}>{matchReasonCopy(match)}</p>;
  }

  if (variant === 'why') {
    return (
      <div
        className={`overflow-hidden rounded-lg bg-white text-left ${
          expanded ? 'h-full min-h-0 w-full px-3 py-3' : 'w-full px-2 py-1.5'
        }`}
      >
        <p className="font-albert-sans text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {isStrong ? 'Strong' : 'Related'} · {source}
        </p>
        {match.match_reason === 'story' ? (
          <>
            <p className="font-open-sans text-primary mt-1 line-clamp-2 break-words text-sm font-medium leading-5">
              {storyTitle || 'Matched in a story'}
            </p>
            {snippet ? (
              <MatchSnippetText
                html={snippet}
                className={`mt-1 block break-words text-sm italic leading-5 text-gray-600 ${expanded ? 'line-clamp-6' : 'line-clamp-2'}`}
              />
            ) : null}
          </>
        ) : match.match_reason === 'tag' ? (
          <p className="font-open-sans text-primary mt-1 line-clamp-3 break-words text-sm leading-5">
            {snippet ? <MatchSnippetText html={snippet} /> : 'Matched on a topic'}
          </p>
        ) : match.match_reason === 'bio' || match.match_reason === 'summary' || match.match_reason === 'transcript' ? (
          <>
            <p className="font-open-sans text-primary mt-1 text-sm leading-5">{matchReasonCopy(match)}</p>
            {snippet ? (
              <MatchSnippetText
                html={snippet}
                className={`mt-1 block break-words text-sm leading-5 text-gray-600 ${expanded ? 'line-clamp-5' : 'line-clamp-2'}`}
              />
            ) : null}
          </>
        ) : (
          <p className="font-open-sans text-primary mt-1 text-sm leading-5">{matchReasonCopy(match)}</p>
        )}
      </div>
    );
  }

  return null;
};

/** Compact match chrome for vertical story/resource tiles (chip / snippet / why under title). */
export const ExperienceMatchHint = ({
  match,
  variant = 'chip',
}: {
  match: SearchMatchFields;
  variant?: MatchStyleVariant;
}) => {
  if (variant === 'none' || !hasSearchMatch(match)) {
    return null;
  }
  if (variant === 'ribbon') {
    return (
      <div className="w-full overflow-hidden rounded-md">
        <PeerListenerMatchRibbon match={match} />
      </div>
    );
  }
  return (
    <PeerListenerMatchHint match={match} variant={variant === 'snippet' || variant === 'why' ? variant : 'chip'} />
  );
};
